/**
 * Plan-derived flow order (KYCID-FSD-00013 §8).
 *
 * The customer flow is a spine of FRAME steps with the frozen plan's PRE_CALL
 * checks spliced in between consent and the waiting room. This module is the
 * pure builder — no store, no React — so it unit-tests cleanly and the store
 * simply calls it.
 *
 *   buildFlowOrder(plan) = FRAME_HEAD ++ (PRE_CALL checks, requirement≠OFF, by order) ++ FRAME_TAIL
 *
 * `consent` stays a FRAME step (it is a precondition, not a check). The
 * relative `indexOf` in the step machine means a check appearing/disappearing
 * re-indexes the stepper with no off-by-one.
 *
 * NOTE on the fail-safe: an EMPTY plan here yields the frame-only spine. The
 * store deliberately treats an empty plan as "keep today's legacy flow" (the
 * documented fail-safe) — see `initCheckPlan`. buildFlowOrder is the honest
 * pure function for a NON-empty server plan.
 */

import type { CheckKey, CheckStepId, PlanCheckDTO } from '@/types';
import { checkStepId } from '@/types';

/**
 * The PRE_CALL checks the CUSTOMER acts on in droom (they have a bespoke customer screen). Every other check
 * is AGENT-driven — done live on the call — and must NOT be rendered as an empty customer step (KYCID·00001-
 * 00005-00017). Kept as a PURE set here (checkPlan must not import the React checkRegistry — it would form a
 * cycle checkPlan→registry→Aadhaar→store→checkPlan); a coverage test pins it to the registry's bespoke
 * components so the two cannot drift.
 */
/**
 * PROVENANCE REALIGNMENT (KYCID·00001-00006-00002, PH-1). Pre-call carries ONLY checks whose result
 * comes from an AUTHORITATIVE SOURCE the customer cannot author — a registry answer cannot be faked
 * by the person being verified, so it is safe to obtain unsupervised. Everything the customer
 * PRODUCES (document photo, selfie, selfie-with-document) moved IN-CALL under the officer's
 * observation, because an unobserved capture cannot distinguish a live person from a printed photo,
 * a screen replay, or someone else holding the phone.
 *
 * Removing a key here stops it RENDERING as a customer step; the backend pinning it IN_CALL stops it
 * being EXPECTED pre-call. Both are needed — this set alone would hide a step the queue gate still
 * demanded, which is the half-done phase move the coverage invariant in checkRegistry exists to catch.
 */
export const CUSTOMER_FACING_PRECALL_CHECKS: ReadonlySet<CheckKey> = new Set<CheckKey>([
  'DIGILOCKER_AADHAAR',
  // DIGILOCKER_PAN is deliberately ABSENT despite being an authoritative pre-call rail under PH-1:
  // no bespoke droom step exists for it yet, and the coverage invariant in checkRegistry requires
  // this set and CHECK_COMPONENTS to match EXACTLY. Listing it here without a component would throw
  // at dev load — correctly, because it would otherwise render the MissingCheckStep placeholder to a
  // customer. It joins this set in the same change that builds its step.
  // Owner item 3: PAN as a pre-check. Source-of-record, so the customer cannot author the answer.
  'PAN_SOURCE',
]);

/** The spine (mirror of the BE frame). `consent` is a precondition, stays frame.
 *  `confirm` (SPEC-14 §5) is the golden-source intake confirm — a frame step
 *  because it is a precondition ceremony, not a check; it self-skips when the
 *  session has no intake. */
export type FrameStepId =
  | 'welcome'
  | 'schedule'
  | 'consent'
  | 'confirm'
  | 'waiting'
  | 'call'
  | 'complete';

/** A position in the plan-derived flow: a frame step or a keyed check step. */
export type PlanStepId = FrameStepId | CheckStepId;

/** Frame steps before the PRE_CALL checks (confirm sits before every document/capture step). */
export const FRAME_HEAD: readonly FrameStepId[] = ['welcome', 'schedule', 'consent', 'confirm'];

/** Frame steps after the PRE_CALL checks. */
export const FRAME_TAIL: readonly FrameStepId[] = ['waiting', 'call', 'complete'];

/** Stepper-visible head (welcome + schedule are pre-stepper entry screens). */
const STEPPER_HEAD: readonly FrameStepId[] = ['consent'];

/** The PRE_CALL checks that actually run, in pinned order. */
function activePreCallChecks(plan: readonly PlanCheckDTO[]): readonly PlanCheckDTO[] {
  return plan
    .filter(
      (p) =>
        p.phase === 'PRE_CALL' &&
        p.requirement !== 'OFF' &&
        // Only render checks the CUSTOMER acts on. Agent-driven checks (PAN_SOURCE, CONSENT, …) run live on
        // the call and must NOT appear as empty customer steps (KYCID·00001-00005-00017).
        CUSTOMER_FACING_PRECALL_CHECKS.has(p.checkKey),
    )
    .slice()
    .sort((a, b) => a.order - b.order);
}

/** FRAME_HEAD ++ PRE_CALL check steps ++ FRAME_TAIL. */
export function buildFlowOrder(plan: readonly PlanCheckDTO[]): readonly PlanStepId[] {
  const checks = activePreCallChecks(plan).map((p) => checkStepId(p.checkKey));
  return [...FRAME_HEAD, ...checks, ...FRAME_TAIL];
}

/** The stepper subset (excludes the welcome/schedule entry screens). */
export function buildStepperSteps(plan: readonly PlanCheckDTO[]): readonly PlanStepId[] {
  const checks = activePreCallChecks(plan).map((p) => checkStepId(p.checkKey));
  return [...STEPPER_HEAD, ...checks, ...FRAME_TAIL];
}
